import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Grid,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';

import CheckBox from 'components/CheckBox';
import LocationSelect from 'components/Select';
import DatePickers from 'Views/Common/DatePickers';
import { REVSHARE_SERVICES } from 'Services';
import {
  CHARGE_DATE,
  CHARGE_LEVEL,
  ENABLED_PC_MODES,
  PLATFORM_COST_MODE,
  PLATFORM_COST_MODE_OPTIONS,
  REVENUE_SHARE_OPTIONS,
} from 'Views/Common/enum';
import Utils from 'Shared/Utils';
import { useCountryConfig } from 'hooks/useCountryConfig';

import { recomputeFirstUnits, appendTierRow, getPayoutTypes } from './platformCostUtils';
import styles from './PlatformCostFields.module.scss';
import useStyles from './styles';

/**
 * Controlled presentational component that renders the whole Platform Cost UI
 * (mode dropdown, charge fields, tier pricing table, revenue share). It owns no
 * load/save logic — the parent supplies `value` and receives updates via
 * `onChange(nextValue)`.
 */
const PlatformCostFields = ({ value, onChange, locationList = [], allowLocationLevel = true }) => {
  const { symbol = '$' } = useCountryConfig();
  const payoutTypes = useMemo(() => getPayoutTypes(symbol), [symbol]);
  const classes = useStyles();

  const [revshareList, setRevshareList] = useState([]);

  const {
    pcMode,
    pcLevel,
    pcChargeDay,
    pcFlatAmount,
    pcTiers,
    pcStartDate,
    pcLocationIds,
    revshareEnabled,
    revshareId,
    revshareType,
    revshareValue,
  } = value;

  const patch = (fields) => onChange({ ...value, ...fields });

  // ── Fetch revshare list (once on mount) ──────────────────────
  useEffect(() => {
    const fetchRevshareList = async () => {
      try {
        const response = await REVSHARE_SERVICES.fetchRevshareByQueryParams();
        if (Utils.checkIfSuccess(response)) {
          setRevshareList(Utils.getValue(response?.data?.rows, []));
        }
      } catch (err) {
        console.error(err || 'Error while fetching the list of revshare partners');
      }
    };
    fetchRevshareList();
  }, []);

  // Auto-select first revshare when enabling revshare and none selected
  useEffect(() => {
    if (revshareEnabled && !revshareId && revshareList.length) {
      onChange({ ...value, revshareId: revshareList[0]?.revshare_id });
    }
  }, [revshareEnabled, revshareId, revshareList]);

  const handlePcModeChange = (e) => {
    const newMode = e.target.value;
    const next = { pcMode: newMode };
    const validKeys = CHARGE_LEVEL.filter((level) => level.modes.includes(newMode)).map((l) => l.key);
    if (ENABLED_PC_MODES.includes(newMode) && !validKeys.includes(pcLevel)) {
      next.pcLevel = 'group';
      next.pcLocationIds = [];
    }
    patch(next);
  };

  const handleTierChange = (index, field, val) => {
    const updated = pcTiers.map((t, i) => (i === index ? { ...t, [field]: val } : t));
    patch({ pcTiers: recomputeFirstUnits(updated) });
  };

  const addTierRow = () => patch({ pcTiers: appendTierRow(pcTiers) });

  const removeTierRow = (index) => {
    if (pcTiers.length <= 2) return;
    const updated = pcTiers.filter((_, i) => i !== index);
    updated[updated.length - 1] = { ...updated[updated.length - 1], last_unit: null };
    patch({ pcTiers: recomputeFirstUnits(updated) });
  };

  return (
    <Grid container spacing={3}>
      {/* Platform Cost dropdown + config fields on one row */}
      <Grid item xs={12}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Choose your pricing model</InputLabel>
              <Select
                value={pcMode}
                onChange={handlePcModeChange}
                label="Choose your pricing model"
                data-testid="platform-cost-mode-select"
              >
                {PLATFORM_COST_MODE_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    value={opt.key}
                    data-testid={`platform-cost-mode-${opt.key}`}
                  >
                    {opt.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {ENABLED_PC_MODES.includes(pcMode) && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Charge Level</InputLabel>
                  <Select
                    id="platform-cost-charge-level"
                    label="Charge Level*"
                    value={pcLevel}
                    onChange={(e) => {
                      const level = e.target.value;
                      patch({
                        pcLevel: level,
                        ...(level !== 'location' ? { pcLocationIds: [] } : {}),
                      });
                    }}
                  >
                    {CHARGE_LEVEL.filter(
                      (level) =>
                        level.modes.includes(pcMode) &&
                        (allowLocationLevel || level.key !== 'location'),
                    ).map((elm) => (
                      <MenuItem
                        key={elm?.key}
                        value={elm?.key}
                        data-testid={`platform-cost-charge-level-${elm?.key}`}
                      >
                        {elm?.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {allowLocationLevel && (
              <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                <FormControl fullWidth variant="outlined" disabled={pcLevel !== 'location'}>
                  <InputLabel>Locations</InputLabel>
                  <LocationSelect
                    multiple
                    label="Locations"
                    value={pcLocationIds}
                    onChange={(e) => patch({ pcLocationIds: e.target.value })}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None selected';
                      return locationList
                        .filter((loc) => selected.includes(loc.location_id))
                        .map((loc) => loc.location_name)
                        .join(', ');
                    }}
                    data-testid="platform-cost-locations-select"
                  >
                    {locationList.map((loc, index) => (
                      <MenuItem
                        key={loc.location_id}
                        value={loc.location_id}
                        data-testid={`platform-cost-location-${index}`}
                      >
                        <div className={styles.locationOption}>
                          <CheckBox
                            checked={pcLocationIds.includes(loc.location_id)}
                            readOnly
                            size="medium"
                            className={styles.locationCheckbox}
                          />
                          {loc.location_name}
                        </div>
                      </MenuItem>
                    ))}
                  </LocationSelect>
                </FormControl>
              </Grid>
              )}
              <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                <DatePickers
                  fullWidth
                  required
                  autoOk
                  variant="inline"
                  inputVariant="outlined"
                  label="Billing Start Date"
                  format="MM/dd/yyyy"
                  placeholder="MM/DD/YYYY"
                  value={pcStartDate ? moment(pcStartDate, 'YYYY-MM-DD').toDate() : null}
                  disablePast
                  invalidDateMessage=""
                  minDateMessage=""
                  maxDateMessage=""
                  onChange={(date) =>
                    patch({ pcStartDate: date ? moment(date).format('YYYY-MM-DD') : '' })
                  }
                  KeyboardButtonProps={{
                    'aria-label': 'change billing start date',
                    'data-testid': 'platform-cost-start-date-button',
                  }}
                  inputProps={{ 'data-testid': 'platform-cost-start-date' }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Charge Date</InputLabel>
                  <Select
                    id="platform-cost-charge-date"
                    label="Charge Date*"
                    value={pcChargeDay}
                    onChange={(e) => patch({ pcChargeDay: Number(e.target.value) })}
                  >
                    {CHARGE_DATE.map((elem) => (
                      <MenuItem
                        key={elem?.key}
                        value={elem?.key}
                        data-testid={`platform-cost-charge-date-${elem?.key}`}
                      >
                        {elem?.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {pcMode === PLATFORM_COST_MODE.FLAT && (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    variant="outlined"
                    label="Charged Amount"
                    name="platform_cost_amount"
                    value={pcFlatAmount}
                    onChange={(e) => {
                      let val = Number(e?.target?.value);
                      if (val <= 0) val = 1;
                      patch({ pcFlatAmount: val });
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
                      inputProps: { min: 1, 'data-testid': 'platform-cost-amount-input' },
                    }}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {/* Tier Table — own row */}
      {pcMode === PLATFORM_COST_MODE.TIER && (
        <Grid item xs={12}>
          <div className={styles.tierTableWrapper}>
            <Table size="small">
              <TableHead>
                {/* Unified section header row */}
                <TableRow>
                  <TableCell colSpan={4} className={styles.tierHeaderCell}>
                    <Typography variant="body1">Pricing Tiers</Typography>
                  </TableCell>
                </TableRow>
                {/* Column label row */}
                <TableRow>
                  <TableCell className={styles.tierSubHeaderCell}>
                    <Typography variant="body1">First Unit</Typography>
                  </TableCell>
                  <TableCell className={styles.tierSubHeaderCell}>
                    <Typography variant="body1">Last Unit</Typography>
                  </TableCell>
                  <TableCell className={styles.tierSubHeaderCell}>
                    <Typography variant="body1">Platform Cost</Typography>
                  </TableCell>
                  <TableCell className={styles.tierSubHeaderSpacer} />
                </TableRow>
              </TableHead>
              <TableBody>
                {pcTiers.map((row, i) => {
                  const isLastRow = i === pcTiers.length - 1;
                  return (
                    <TableRow key={row._id}>
                      <TableCell className={styles.tierDataCell}>
                        <TextField
                          value={row.first_unit}
                          disabled
                          variant="outlined"
                          size="small"
                          inputProps={{ 'data-testid': `tier-first-unit-${i}` }}
                        />
                      </TableCell>
                      <TableCell className={styles.tierDataCell}>
                        {isLastRow ? (
                          <TextField
                            value="∞"
                            disabled
                            variant="outlined"
                            size="small"
                            inputProps={{ 'data-testid': `tier-last-unit-${i}` }}
                          />
                        ) : (
                          <TextField
                            type="number"
                            value={row.last_unit ?? ''}
                            onChange={(e) =>
                              handleTierChange(i, 'last_unit', Number(e.target.value))
                            }
                            variant="outlined"
                            size="small"
                            inputProps={{
                              min: row.first_unit,
                              'data-testid': `tier-last-unit-${i}`,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell className={styles.tierDataCell}>
                        <TextField
                          type="number"
                          value={row.amount}
                          onChange={(e) => handleTierChange(i, 'amount', Number(e.target.value))}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">{symbol}</InputAdornment>
                            ),
                          }}
                          inputProps={{ min: 0, 'data-testid': `tier-amount-${i}` }}
                        />
                      </TableCell>
                      <TableCell className={styles.tierDataCell}>
                        {i > 0 && pcTiers.length > 2 && (
                          <IconButton
                            disableRipple
                            size="small"
                            onClick={() => removeTierRow(i)}
                            data-testid={`tier-remove-row-${i}`}
                          >
                            <DeleteIcon className="theme_color_orange" fontSize="inherit" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className={styles.tierAddButtonContainer}>
              <Button
                onClick={addTierRow}
                size="small"
                data-testid="tier-add-row-button"
                className={styles.tierAddButton}
              >
                + Add Another Tier
              </Button>
            </div>
          </div>
        </Grid>
      )}

      {/* Revenue Share — dropdown + fields on one row */}
      {ENABLED_PC_MODES.includes(pcMode) && (
        <Grid item xs={12}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Revenue Share</InputLabel>
                <Select
                  value={revshareEnabled ? 'enabled' : 'disabled'}
                  onChange={(e) => patch({ revshareEnabled: e.target.value === 'enabled' })}
                  label="Revenue Share"
                  data-testid="revenue-share-select"
                >
                  {REVENUE_SHARE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {revshareEnabled && (
              <>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                  <FormControl fullWidth required variant="outlined">
                    <InputLabel>Revshare Name</InputLabel>
                    <Select
                      id="platform-revshare-id"
                      label="Revshare Name*"
                      value={revshareId}
                      onChange={(e) => patch({ revshareId: e.target.value })}
                    >
                      {revshareList?.map((vendor, i) => (
                        <MenuItem
                          value={vendor?.revshare_id}
                          key={vendor?.revshare_id}
                          data-testid={`platform-revshare-select-item-${i}`}
                        >
                          {vendor?.revshare_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="Commission"
                    value={revshareValue}
                    onChange={(e) => {
                      let val = Number(e?.target?.value);
                      if (val <= 0 || Number.isNaN(val)) val = 1;
                      patch({ revshareValue: val });
                    }}
                    InputProps={{
                      inputProps: { 'data-testid': 'platform-revshare-amount-input' },
                      endAdornment: (
                        <Grid container className={classes.tabsContainer}>
                          <Tabs
                            value={revshareType}
                            indicatorColor="secondary"
                            textColor="secondary"
                            onChange={(_evt, val) => patch({ revshareType: val })}
                            className={classes.discountTabs}
                          >
                            {payoutTypes.map((indType) => (
                              <Tab
                                key={indType?.value}
                                value={indType?.value}
                                label={indType?.label}
                              />
                            ))}
                          </Tabs>
                        </Grid>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

PlatformCostFields.propTypes = {
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  locationList: PropTypes.array,
  allowLocationLevel: PropTypes.bool,
};

export default PlatformCostFields;
